import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Building2, 
  Scale, 
  Shield, 
  FileText, 
  Users, 
  Mail, 
  HelpCircle,
  Globe,
  User,
  MapPin,
  Hash,
  CreditCard,
  BookOpen,
  Target,
  Heart,
  Lightbulb,
  Handshake,
  Send,
  Clock,
  Lock,
  Database,
  Cookie,
  AlertCircle,
  CheckCircle,
  Gift,
  RotateCcw,
  DoorOpen,
  Gavel,
  Phone,
  Server,
  Copyright,
  Eye,
  Trash2,
  Download,
  Ban,
  FileCheck
} from "lucide-react";

// Modern Legal Page Layout Component
const ModernLegalPage = ({ title, subtitle, icon: Icon, iconBg = "from-violet-600 to-pink-500", children }) => (
  <div className="min-h-screen bg-slate-50">
    {/* Header */}
    <div className={`bg-gradient-to-br ${iconBg} text-white py-20`}>
      <div className="max-w-4xl mx-auto px-4">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white/90 hover:text-white mb-8 transition-all text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour à l'accueil</span>
        </Link>
        <div className="flex items-center gap-5">
          {Icon && (
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
              <Icon className="w-8 h-8 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-4xl md:text-5xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight">{title}</h1>
            {subtitle && <p className="text-white/70 mt-2 text-lg">{subtitle}</p>}
          </div>
        </div>
      </div>
    </div>
    
    {/* Content */}
    <div className="max-w-4xl mx-auto px-4 py-12 -mt-6">
      {children}
    </div>
  </div>
);

// Section Card Component
const SectionCard = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden mb-6 hover:shadow-md transition-shadow">
    {title && (
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
        {Icon && (
          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <Icon className="w-5 h-5 text-slate-600" />
          </div>
        )}
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      </div>
    )}
    <div className="p-6 prose prose-slate max-w-none prose-headings:text-slate-900 prose-a:text-violet-600 prose-a:no-underline hover:prose-a:underline">
      {children}
    </div>
  </div>
);

// Info Card Component
const InfoCard = ({ label, value, icon: Icon }) => (
  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 hover:border-slate-200 transition-colors">
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-slate-500" />
        </div>
      )}
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">{label}</p>
        <p className="font-semibold text-slate-900 mt-0.5">{value}</p>
      </div>
    </div>
  </div>
);

export const MentionsLegales = () => (
  <ModernLegalPage 
    title="Mentions légales" 
    subtitle="Informations légales relatives au site ResponsIA"
    icon={Scale}
    iconBg="from-slate-800 to-slate-900"
  >
    <SectionCard title="Éditeur du site" icon={Building2}>
      <div className="grid md:grid-cols-2 gap-4 not-prose">
        <InfoCard label="Raison sociale" value="ResponsIA - Sabrina ADJARSA" icon={User} />
        <InfoCard label="Statut" value="Entrepreneur individuel" icon={FileText} />
        <InfoCard label="Adresse" value="52 Boulevard Murat, 75016 Paris" icon={MapPin} />
        <InfoCard label="Email" value="contact@responsia.fr" icon={Mail} />
        <InfoCard label="Téléphone" value="06 66 02 79 25" icon={Phone} />
        <InfoCard label="SIREN" value="914 310 966" icon={Hash} />
        <InfoCard label="SIRET" value="914 310 966 00025" icon={Hash} />
        <InfoCard label="Code APE" value="62.01Z - Programmation informatique" icon={FileCheck} />
        <InfoCard label="TVA" value="Non applicable (art. 293 B du CGI)" icon={CreditCard} />
      </div>
    </SectionCard>

    <SectionCard title="Directrice de la publication" icon={User}>
      <p><strong>Sabrina ADJARSA</strong></p>
    </SectionCard>

    <SectionCard title="Hébergeur" icon={Server}>
      <div className="grid md:grid-cols-2 gap-4 not-prose">
        <InfoCard label="Hébergeur" value="Railway Corporation" icon={Globe} />
        <InfoCard label="Adresse" value="548 Market St, San Francisco, CA 94104, USA" icon={MapPin} />
      </div>
    </SectionCard>

    <SectionCard title="Propriété intellectuelle" icon={Copyright}>
      <p>
        L'ensemble du contenu de ce site (textes, images, graphismes, logo, icônes, etc.) 
        est la propriété exclusive de ResponsIA ou de ses partenaires. Toute reproduction, 
        représentation, modification, publication, transmission ou dénaturation, totale ou 
        partielle du site ou de son contenu, par quelque procédé que ce soit, et sur quelque 
        support que ce soit est interdite sans l'autorisation écrite préalable de ResponsIA.
      </p>
    </SectionCard>

    <SectionCard title="Protection des données personnelles" icon={Shield}>
      <p>
        Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez 
        d'un droit d'accès, de rectification, d'effacement et de portabilité de vos données 
        personnelles. Pour exercer ces droits, contactez-nous à{" "}
        <a href="mailto:contact@responsia.fr">contact@responsia.fr</a>.
      </p>
      <p className="mt-4">
        Pour plus d'informations, consultez notre{" "}
        <Link to="/confidentialite">Politique de Confidentialité</Link>.
      </p>
    </SectionCard>
  </ModernLegalPage>
);

export const CGU = () => (
  <ModernLegalPage 
    title="Conditions Générales d'Utilisation" 
    subtitle="Règles d'utilisation de la plateforme ResponsIA"
    icon={FileText}
    iconBg="from-blue-600 to-blue-800"
  >
    <SectionCard title="Objet" icon={Target}>
      <p>
        Les présentes Conditions Générales d'Utilisation (CGU) définissent les règles 
        d'accès et d'utilisation de la plateforme ResponsIA, accessible à l'adresse 
        <strong> app.responsia.fr</strong>.
      </p>
      <p>
        En utilisant nos services, vous acceptez sans réserve les présentes CGU. Si vous 
        n'acceptez pas ces conditions, vous ne devez pas utiliser la plateforme.
      </p>
    </SectionCard>

    <SectionCard title="Description des services" icon={Lightbulb}>
      <p>ResponsIA propose une plateforme de gestion de la e-réputation permettant de :</p>
      <ul>
        <li>Générer un QR code personnalisé pour collecter des avis clients</li>
        <li>Rediriger automatiquement les avis positifs vers Google</li>
        <li>Conserver les avis négatifs de manière privée pour amélioration</li>
        <li>Analyser les retours clients via un tableau de bord</li>
        <li>Recevoir des recommandations d'amélioration générées par IA</li>
      </ul>
    </SectionCard>

    <SectionCard title="Inscription et compte utilisateur" icon={User}>
      <p>
        Pour accéder aux services, vous devez créer un compte en fournissant des 
        informations exactes et complètes. Vous êtes responsable de la confidentialité 
        de vos identifiants de connexion.
      </p>
      <p>
        Vous vous engagez à nous informer immédiatement de toute utilisation non 
        autorisée de votre compte.
      </p>
    </SectionCard>

    <SectionCard title="Obligations de l'utilisateur" icon={CheckCircle}>
      <p>En utilisant ResponsIA, vous vous engagez à :</p>
      <ul>
        <li>Utiliser la plateforme conformément à sa destination</li>
        <li>Ne pas collecter de faux avis ou inciter à de fausses déclarations</li>
        <li>Respecter les droits de propriété intellectuelle de ResponsIA</li>
        <li>Ne pas tenter de contourner les mesures de sécurité</li>
        <li>Ne pas utiliser la plateforme à des fins illégales</li>
      </ul>
    </SectionCard>

    <SectionCard title="Propriété intellectuelle" icon={Copyright}>
      <p>
        Tous les éléments de la plateforme (design, textes, code, fonctionnalités) sont 
        protégés par le droit de la propriété intellectuelle. Toute reproduction ou 
        utilisation non autorisée est strictement interdite.
      </p>
    </SectionCard>

    <SectionCard title="Limitation de responsabilité" icon={AlertCircle}>
      <p>
        ResponsIA s'efforce d'assurer la disponibilité et le bon fonctionnement de la 
        plateforme. Toutefois, nous ne pouvons garantir une disponibilité ininterrompue 
        ni l'absence totale de bugs ou erreurs.
      </p>
      <p>
        ResponsIA ne saurait être tenu responsable des dommages indirects résultant de 
        l'utilisation ou de l'impossibilité d'utiliser la plateforme.
      </p>
    </SectionCard>

    <SectionCard title="Modification des CGU" icon={FileText}>
      <p>
        ResponsIA se réserve le droit de modifier les présentes CGU à tout moment. Les 
        utilisateurs seront informés des modifications importantes par email ou via la 
        plateforme. La poursuite de l'utilisation après modification vaut acceptation.
      </p>
    </SectionCard>

    <SectionCard title="Contact" icon={Mail}>
      <p>
        Pour toute question relative aux présentes CGU :<br />
        <strong>Email :</strong> <a href="mailto:contact@responsia.fr">contact@responsia.fr</a><br />
        <strong>Adresse :</strong> 52 Boulevard Murat, 75016 Paris, France
      </p>
    </SectionCard>
  </ModernLegalPage>
);

export const PolitiqueConfidentialite = () => (
  <ModernLegalPage 
    title="Politique de Confidentialité" 
    subtitle="Comment nous protégeons vos données personnelles"
    icon={Shield}
    iconBg="from-emerald-600 to-emerald-800"
  >
    <SectionCard title="Responsable du traitement" icon={Building2}>
      <div className="grid md:grid-cols-2 gap-4 not-prose">
        <InfoCard label="Responsable" value="ResponsIA - Sabrina ADJARSA" icon={User} />
        <InfoCard label="Email DPO" value="contact@responsia.fr" icon={Mail} />
        <InfoCard label="Adresse" value="52 Boulevard Murat, 75016 Paris" icon={MapPin} />
      </div>
    </SectionCard>

    <SectionCard title="Données collectées" icon={Database}>
      <p>Nous collectons les données suivantes :</p>
      <ul>
        <li><strong>Données d'identification :</strong> nom, prénom, email</li>
        <li><strong>Données d'établissement :</strong> nom, type, adresse, Google Place ID</li>
        <li><strong>Données d'avis :</strong> notes, commentaires des clients</li>
        <li><strong>Données de connexion :</strong> logs, adresse IP, navigateur</li>
        <li><strong>Données de paiement :</strong> traitées par Stripe (nous ne stockons pas vos données bancaires)</li>
      </ul>
    </SectionCard>

    <SectionCard title="Finalités du traitement" icon={Target}>
      <p>Vos données sont utilisées pour :</p>
      <ul>
        <li>Fournir et améliorer nos services</li>
        <li>Gérer votre compte et votre abonnement</li>
        <li>Vous envoyer des communications relatives au service</li>
        <li>Générer des analyses et recommandations personnalisées</li>
        <li>Assurer la sécurité de la plateforme</li>
      </ul>
    </SectionCard>

    <SectionCard title="Base légale" icon={Gavel}>
      <ul>
        <li><strong>Exécution du contrat :</strong> pour la fourniture de nos services</li>
        <li><strong>Consentement :</strong> pour les emails marketing (optionnel)</li>
        <li><strong>Intérêt légitime :</strong> pour l'amélioration de nos services</li>
        <li><strong>Obligation légale :</strong> pour les factures et données comptables</li>
      </ul>
    </SectionCard>

    <SectionCard title="Durée de conservation" icon={Clock}>
      <ul>
        <li><strong>Données de compte :</strong> conservées pendant la durée de votre abonnement + 3 ans</li>
        <li><strong>Données de facturation :</strong> conservées 10 ans (obligation légale)</li>
        <li><strong>Logs de connexion :</strong> conservés 1 an</li>
        <li><strong>Données des avis :</strong> conservées pendant la durée du compte</li>
      </ul>
    </SectionCard>

    <SectionCard title="Vos droits (RGPD)" icon={Shield}>
      <p>Conformément au RGPD, vous disposez des droits suivants :</p>
      <ul>
        <li><strong>Accès :</strong> obtenir une copie de vos données</li>
        <li><strong>Rectification :</strong> corriger vos données inexactes</li>
        <li><strong>Effacement :</strong> demander la suppression de vos données</li>
        <li><strong>Portabilité :</strong> récupérer vos données dans un format standard</li>
        <li><strong>Opposition :</strong> vous opposer au traitement de vos données</li>
        <li><strong>Limitation :</strong> limiter le traitement de vos données</li>
      </ul>
      <p className="mt-4">
        Pour exercer ces droits, contactez-nous à{" "}
        <a href="mailto:contact@responsia.fr">contact@responsia.fr</a>.
      </p>
    </SectionCard>

    <SectionCard title="Sécurité des données" icon={Lock}>
      <p>Nous mettons en œuvre les mesures suivantes pour protéger vos données :</p>
      <ul>
        <li>Chiffrement HTTPS pour toutes les communications</li>
        <li>Mots de passe hashés avec bcrypt</li>
        <li>Serveurs hébergés en Europe (conformité RGPD)</li>
        <li>Accès restreint aux données personnelles</li>
        <li>Sauvegardes régulières et sécurisées</li>
      </ul>
    </SectionCard>

    <SectionCard title="Cookies" icon={Cookie}>
      <p>
        Nous utilisons des cookies essentiels au fonctionnement du site et des cookies 
        analytiques pour améliorer nos services. Vous pouvez gérer vos préférences via 
        le bandeau de consentement.
      </p>
    </SectionCard>

    <SectionCard title="Contact" icon={Mail}>
      <p>
        Pour toute question relative à cette politique ou à vos données personnelles :<br />
        <strong>Email :</strong> <a href="mailto:contact@responsia.fr">contact@responsia.fr</a><br />
        <strong>Adresse :</strong> 52 Boulevard Murat, 75016 Paris, France
      </p>
      <p className="mt-4">
        Vous pouvez également introduire une réclamation auprès de la CNIL : 
        <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="ml-1">www.cnil.fr</a>
      </p>
    </SectionCard>
  </ModernLegalPage>
);

export const CGV = () => (
  <ModernLegalPage 
    title="Conditions Générales de Vente" 
    subtitle="Conditions applicables aux abonnements ResponsIA"
    icon={FileText}
    iconBg="from-orange-500 to-orange-700"
  >
    <SectionCard title="Objet" icon={Target}>
      <p>
        Les présentes Conditions Générales de Vente (CGV) définissent les droits et 
        obligations des parties dans le cadre de la souscription aux services payants 
        proposés par ResponsIA.
      </p>
    </SectionCard>

    <SectionCard title="Services proposés" icon={Lightbulb}>
      <p>ResponsIA propose un abonnement mensuel donnant accès à :</p>
      <ul>
        <li>QR code personnalisé pour la collecte d'avis</li>
        <li>Tableau de bord avec KPIs et statistiques</li>
        <li>Système de redirection automatique vers Google</li>
        <li>Plan d'action IA basé sur les avis négatifs</li>
        <li>Rapports hebdomadaires par email</li>
        <li>Support client par email</li>
      </ul>
    </SectionCard>

    <SectionCard title="Prix et paiement" icon={CreditCard}>
      <div className="bg-gradient-to-r from-violet-50 to-violet-100 rounded-xl p-6 border border-violet-200 mb-4 not-prose">
        <p className="text-3xl font-bold text-violet-900">49€ <span className="text-lg font-medium text-violet-600">/ mois</span></p>
        <p className="text-sm text-violet-700 mt-1">TVA non applicable (art. 293 B du CGI)</p>
      </div>
      <ul>
        <li>Paiement mensuel par carte bancaire via Stripe</li>
        <li>Prélèvement automatique chaque mois à date anniversaire</li>
        <li>Facture disponible dans l'espace client</li>
      </ul>
    </SectionCard>

    <SectionCard title="Essai gratuit" icon={Gift}>
      <p>
        Une période d'essai gratuit de <strong>3 jours</strong> est offerte aux nouveaux 
        utilisateurs. Pendant cette période, vous avez accès à toutes les fonctionnalités.
      </p>
      <ul>
        <li>Carte bancaire requise à l'inscription</li>
        <li>Aucun prélèvement pendant l'essai</li>
        <li>Annulation possible à tout moment pendant l'essai</li>
        <li>L'essai gratuit est limité à une fois par utilisateur</li>
      </ul>
    </SectionCard>

    <SectionCard title="Droit de rétractation" icon={RotateCcw}>
      <p>
        Conformément à l'article L221-28 du Code de la consommation, le droit de 
        rétractation ne s'applique pas aux services pleinement exécutés avant la fin 
        du délai de rétractation et dont l'exécution a commencé avec votre accord.
      </p>
      <p>
        Toutefois, vous pouvez résilier votre abonnement à tout moment depuis votre 
        espace client.
      </p>
    </SectionCard>

    <SectionCard title="Résiliation" icon={DoorOpen}>
      <ul>
        <li>Résiliation possible à tout moment depuis l'espace client</li>
        <li>Aucun frais de résiliation</li>
        <li>Accès maintenu jusqu'à la fin de la période payée</li>
        <li>Pas de remboursement au prorata du mois en cours</li>
      </ul>
    </SectionCard>

    <SectionCard title="Responsabilité" icon={AlertCircle}>
      <p>
        ResponsIA s'engage à fournir les services avec diligence. Toutefois, nous ne 
        pouvons garantir que l'utilisation de nos services entraînera automatiquement 
        une augmentation de vos avis Google ou de votre chiffre d'affaires.
      </p>
      <p>
        Notre responsabilité est limitée au montant des sommes versées au cours des 
        12 derniers mois.
      </p>
    </SectionCard>

    <SectionCard title="Litiges et droit applicable" icon={Gavel}>
      <p>
        Les présentes CGV sont soumises au droit français. En cas de litige, une 
        solution amiable sera recherchée. À défaut, les tribunaux de Paris seront 
        compétents.
      </p>
      <p className="mt-4">
        Conformément à l'article L612-1 du Code de la consommation, vous pouvez 
        recourir à un médiateur de la consommation.
      </p>
    </SectionCard>

    <SectionCard title="Contact" icon={Mail}>
      <p>
        Pour toute question relative aux présentes CGV :<br />
        <strong>Email :</strong> <a href="mailto:contact@responsia.fr">contact@responsia.fr</a><br />
        <strong>Adresse :</strong> 52 Boulevard Murat, 75016 Paris, France
      </p>
    </SectionCard>
  </ModernLegalPage>
);

export const APropos = () => (
  <ModernLegalPage 
    title="À propos de ResponsIA" 
    subtitle="Notre mission : vous aider à transformer vos clients en ambassadeurs"
    icon={Users}
    iconBg="from-violet-600 to-pink-600"
  >
    <SectionCard title="Notre histoire" icon={BookOpen}>
      <p>
        Fondée en 2026 par Sabrina ADJARSA, ResponsIA est née d'un constat simple : 
        les avis en ligne sont devenus essentiels pour les commerces de proximité, 
        mais leur gestion reste complexe et chronophage.
      </p>
      <p>
        Trop d'entreprises perdent des clients potentiels à cause d'une mauvaise 
        e-réputation, alors qu'elles offrent un excellent service. Notre solution 
        permet de collecter plus d'avis positifs tout en transformant les retours 
        négatifs en opportunités d'amélioration.
      </p>
    </SectionCard>

    <SectionCard title="Notre mission" icon={Target}>
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-6 not-prose text-white">
        <p className="text-xl font-semibold mb-2">
          "Aider chaque établissement à rayonner grâce à la voix de ses clients satisfaits"
        </p>
        <p className="text-slate-300">
          Nous croyons que chaque commerce mérite d'être visible et reconnu pour la 
          qualité de son service.
        </p>
      </div>
    </SectionCard>

    <SectionCard title="Nos valeurs" icon={Heart}>
      <div className="grid md:grid-cols-3 gap-4 not-prose">
        <div className="bg-white rounded-xl p-5 border border-slate-200 text-center hover:border-emerald-300 hover:shadow-md transition-all">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Handshake className="w-6 h-6 text-emerald-600" />
          </div>
          <h4 className="font-semibold text-slate-900">Transparence</h4>
          <p className="text-sm text-slate-500 mt-1">Pas de faux avis, pas de pratiques douteuses</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 text-center hover:border-blue-300 hover:shadow-md transition-all">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Lightbulb className="w-6 h-6 text-blue-600" />
          </div>
          <h4 className="font-semibold text-slate-900">Innovation</h4>
          <p className="text-sm text-slate-500 mt-1">L'IA au service de votre croissance</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 text-center hover:border-violet-300 hover:shadow-md transition-all">
          <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Heart className="w-6 h-6 text-violet-600" />
          </div>
          <h4 className="font-semibold text-slate-900">Proximité</h4>
          <p className="text-sm text-slate-500 mt-1">Un support humain et réactif</p>
        </div>
      </div>
    </SectionCard>

    <SectionCard title="L'équipe" icon={Users}>
      <div className="flex items-center gap-5 not-prose">
        <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-pink-500 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg">
          S
        </div>
        <div>
          <h4 className="text-xl font-semibold text-slate-900">Sabrina ADJARSA</h4>
          <p className="text-violet-600 font-medium">Fondatrice & CEO</p>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">
            Passionnée par l'entrepreneuriat et le marketing digital, Sabrina a créé 
            ResponsIA pour démocratiser l'accès aux outils de gestion de la e-réputation.
          </p>
        </div>
      </div>
    </SectionCard>

    <SectionCard title="Nous contacter" icon={Send}>
      <div className="grid md:grid-cols-2 gap-4 not-prose">
        <a 
          href="mailto:contact@responsia.fr" 
          className="flex items-center gap-4 p-5 bg-white rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all group"
        >
          <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center group-hover:bg-violet-200 transition-colors">
            <Mail className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">Email</p>
            <p className="text-sm text-slate-500">contact@responsia.fr</p>
          </div>
        </a>
        <Link 
          to="/faq" 
          className="flex items-center gap-4 p-5 bg-white rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all group"
        >
          <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center group-hover:bg-violet-200 transition-colors">
            <HelpCircle className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">FAQ</p>
            <p className="text-sm text-slate-500">Questions fréquentes</p>
          </div>
        </Link>
      </div>
    </SectionCard>
  </ModernLegalPage>
);
export const FAQ = () => {
  const [openIndex, setOpenIndex] = React.useState(null);
  
  const faqSections = [
    {
      title: "Rentabilité & Résultats",
      icon: Target,
      questions: [
        {
          q: "ResponsIA est-il rentable pour mon établissement ?",
          a: "Absolument ! À 49€/mois (soit 1,60€/jour), ResponsIA est rentabilisé dès le premier nouveau client acquis grâce à vos avis Google. En moyenne, nos utilisateurs observent une augmentation de 68% de leur visibilité Google et x3 plus d'avis collectés."
        },
        {
          q: "Combien de temps avant de voir des résultats ?",
          a: "Les premiers résultats sont visibles dès la première semaine. Vous collecterez vos premiers avis Google dans les jours suivant l'installation de votre QR code. L'amélioration de votre note Google et de votre visibilité se fait progressivement sur 2-4 semaines."
        },
        {
          q: "Comment ResponsIA m'aide à attirer de nouveaux clients ?",
          a: "93% des consommateurs lisent les avis en ligne avant de choisir un établissement. Plus vous avez d'avis positifs récents sur Google, plus vous apparaissez en haut des recherches locales, et plus les clients vous font confiance. C'est un cercle vertueux !"
        }
      ]
    },
    {
      title: "Fonctionnement",
      icon: Lightbulb,
      questions: [
        {
          q: "Comment fonctionne la redirection vers Google ?",
          a: "C'est simple et automatique ! Quand un client scanne votre QR code et donne 4 ou 5 étoiles, il est instantanément redirigé vers votre fiche Google pour y publier son avis. S'il donne moins de 4 étoiles, son avis reste privé et vous recevez un feedback pour vous améliorer."
        },
        {
          q: "Que deviennent les avis négatifs ?",
          a: "Les avis de 1 à 3 étoiles ne sont JAMAIS publiés sur Google. Ils restent 100% privés et sont analysés par notre IA pour vous fournir un plan d'action concret avec des recommandations d'amélioration, des estimations de coûts et des priorités."
        },
        {
          q: "Comment fonctionne le Plan d'Action IA ?",
          a: "Notre IA analyse automatiquement vos avis négatifs pour identifier les problèmes récurrents (temps d'attente, accueil, qualité...). Elle génère ensuite des recommandations personnalisées avec des actions concrètes, des coûts estimés et des délais de mise en œuvre. Le plan se met à jour à chaque nouvel avis négatif."
        },
        {
          q: "Dois-je avoir un compte Google Business ?",
          a: "Oui, pour que vos clients puissent laisser un avis sur Google. Si vous n'en avez pas, c'est gratuit et nous pouvons vous guider dans sa création. C'est indispensable pour votre visibilité locale de toute façon !"
        }
      ]
    },
    {
      title: "Abonnement & Essai",
      icon: CreditCard,
      questions: [
        {
          q: "L'essai gratuit de 3 jours est-il vraiment sans engagement ?",
          a: "Oui, 100% sans engagement ! Vous pouvez annuler à tout moment pendant les 3 jours d'essai et vous ne serez jamais débité. Votre carte bancaire est demandée uniquement pour éviter les abus, mais aucun prélèvement n'est effectué pendant l'essai."
        },
        {
          q: "Puis-je annuler mon abonnement à tout moment ?",
          a: "Absolument. Vous pouvez résilier en 2 clics depuis votre espace client, sans justification et sans frais cachés. Votre accès reste actif jusqu'à la fin de la période payée."
        },
        {
          q: "Que se passe-t-il si je me réabonne après avoir annulé ?",
          a: "L'essai gratuit de 3 jours est une offre unique pour les nouveaux utilisateurs. Si vous vous réabonnez après une résiliation, le paiement commence immédiatement à 49€/mois."
        },
        {
          q: "Quels moyens de paiement acceptez-vous ?",
          a: "Nous acceptons toutes les cartes bancaires (Visa, Mastercard, CB) via Stripe, le leader mondial du paiement sécurisé en ligne. Vos données bancaires ne transitent jamais par nos serveurs."
        }
      ]
    },
    {
      title: "Support & Sécurité",
      icon: Shield,
      questions: [
        {
          q: "Mes données sont-elles sécurisées ?",
          a: "Oui, la sécurité est notre priorité. Nous utilisons le chiffrement HTTPS, vos mots de passe sont hashés avec bcrypt, et nos serveurs sont hébergés en Europe (conformité RGPD). Nous ne revendons jamais vos données."
        },
        {
          q: "Comment puis-je contacter le support ?",
          a: "Écrivez-nous à support@responsia.fr. Nous répondons sous 24-48h ouvrées. Vous pouvez également consulter notre documentation en ligne ou la page d'aide de votre tableau de bord."
        },
        {
          q: "Puis-je supprimer mon compte et mes données ?",
          a: "Oui, conformément au RGPD, vous pouvez demander la suppression totale de votre compte et de toutes vos données à tout moment depuis les paramètres de votre compte ou en nous contactant."
        }
      ]
    }
  ];

  const toggleQuestion = (sectionIdx, questionIdx) => {
    const key = `${sectionIdx}-${questionIdx}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-600 to-pink-500 text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white mb-6 transition-all backdrop-blur-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à l'accueil</span>
          </Link>
          <div className="text-center">
            <h1 className="text-4xl font-bold font-['Plus_Jakarta_Sans'] mb-4">
              Questions fréquentes
            </h1>
            <p className="text-lg text-white/80">
              Tout ce que vous devez savoir sur ResponsIA
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {faqSections.map((section, sectionIdx) => (
          <div key={sectionIdx} className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-pink-100 flex items-center justify-center">
                <section.icon className="w-5 h-5 text-violet-600" />
              </div>
              {section.title}
            </h2>
            <div className="space-y-3">
              {section.questions.map((item, questionIdx) => {
                const isOpen = openIndex === `${sectionIdx}-${questionIdx}`;
                return (
                  <div 
                    key={questionIdx}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleQuestion(sectionIdx, questionIdx)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-medium text-slate-900 pr-4">{item.q}</span>
                      <svg 
                        className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-4">
                        <p className="text-slate-600 leading-relaxed">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-center text-white">
          <h3 className="text-xl font-bold mb-2">Vous n'avez pas trouvé votre réponse ?</h3>
          <p className="text-slate-300 mb-4">Notre équipe est là pour vous aider</p>
          <a 
            href="mailto:contact@responsia.fr"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-full font-medium hover:bg-slate-100 transition-colors"
          >
            Contactez-nous
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
};

// ==================== COOKIES PAGE ====================
export const Cookies = () => {
  const [preferences, setPreferences] = React.useState({
    essential: true,
    analytics: false,
    marketing: false
  });
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    // Load saved preferences from localStorage
    const savedPrefs = localStorage.getItem('cookie_preferences');
    if (savedPrefs) {
      try {
        setPreferences(JSON.parse(savedPrefs));
      } catch (e) {
        console.error('Error loading cookie preferences');
      }
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem('cookie_preferences', JSON.stringify(preferences));
    localStorage.setItem('cookie_consent', 'true');
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleAcceptAll = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    localStorage.setItem('cookie_preferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookie_consent', 'true');
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleRejectAll = () => {
    const onlyEssential = { essential: true, analytics: false, marketing: false };
    setPreferences(onlyEssential);
    localStorage.setItem('cookie_preferences', JSON.stringify(onlyEssential));
    localStorage.setItem('cookie_consent', 'true');
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <ModernLegalPage 
      title="Gestion des Cookies" 
      subtitle="Personnalisez vos préférences de cookies"
      icon={Cookie}
      iconBg="from-amber-500 to-orange-600"
    >
      <SectionCard title="Qu'est-ce qu'un cookie ?" icon={HelpCircle}>
        <p>
          Un cookie est un petit fichier texte déposé sur votre appareil (ordinateur, smartphone, tablette) 
          lors de votre visite sur notre site. Il permet de mémoriser vos préférences et d'améliorer 
          votre expérience de navigation.
        </p>
      </SectionCard>

      <SectionCard title="Vos préférences" icon={Cookie}>
        <div className="space-y-4 not-prose">
          {/* Essential Cookies */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Cookies essentiels</h4>
                  <p className="text-sm text-slate-500 mt-1">
                    Nécessaires au fonctionnement du site. Ils permettent l'authentification, 
                    la sécurité et la mémorisation de vos préférences de cookies.
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-full">
                  Toujours actifs
                </span>
              </div>
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Cookies analytiques</h4>
                  <p className="text-sm text-slate-500 mt-1">
                    Nous aident à comprendre comment vous utilisez le site pour l'améliorer. 
                    Données anonymisées et agrégées.
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>
          </div>

          {/* Marketing Cookies */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Cookies marketing</h4>
                  <p className="text-sm text-slate-500 mt-1">
                    Utilisés pour vous proposer des publicités pertinentes et mesurer 
                    l'efficacité de nos campagnes.
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4">
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 transition-colors"
            >
              Enregistrer mes préférences
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
            >
              Tout accepter
            </button>
            <button
              onClick={handleRejectAll}
              className="px-6 py-3 bg-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-300 transition-colors"
            >
              Refuser les optionnels
            </button>
          </div>

          {saved && (
            <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-4 py-3 rounded-xl">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Préférences enregistrées avec succès !</span>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Liste des cookies utilisés" icon={Database}>
        <div className="overflow-x-auto not-prose">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Nom</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Type</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Durée</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Finalité</th>
              </tr>
            </thead>
            <tbody className="text-slate-600">
              <tr className="border-b border-slate-100">
                <td className="py-3 px-4 font-mono text-xs">auth_token</td>
                <td className="py-3 px-4"><span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Essentiel</span></td>
                <td className="py-3 px-4">Session</td>
                <td className="py-3 px-4">Authentification utilisateur</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-3 px-4 font-mono text-xs">cookie_consent</td>
                <td className="py-3 px-4"><span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Essentiel</span></td>
                <td className="py-3 px-4">1 an</td>
                <td className="py-3 px-4">Mémorisation du consentement cookies</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-3 px-4 font-mono text-xs">cookie_preferences</td>
                <td className="py-3 px-4"><span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Essentiel</span></td>
                <td className="py-3 px-4">1 an</td>
                <td className="py-3 px-4">Préférences de cookies détaillées</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-3 px-4 font-mono text-xs">_ga</td>
                <td className="py-3 px-4"><span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Analytique</span></td>
                <td className="py-3 px-4">2 ans</td>
                <td className="py-3 px-4">Google Analytics - Statistiques</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="Comment gérer vos cookies ?" icon={Lightbulb}>
        <p>
          Outre les paramètres ci-dessus, vous pouvez également configurer votre navigateur pour :
        </p>
        <ul>
          <li>Bloquer tous les cookies</li>
          <li>Accepter uniquement les cookies de notre site</li>
          <li>Supprimer les cookies à la fermeture du navigateur</li>
        </ul>
        <p className="mt-4">
          <strong>Attention :</strong> Le blocage de certains cookies peut affecter le fonctionnement 
          du site et limiter les fonctionnalités disponibles.
        </p>
      </SectionCard>

      <SectionCard title="Plus d'informations" icon={Mail}>
        <p>
          Pour toute question concernant notre politique de cookies, contactez-nous à{" "}
          <a href="mailto:contact@responsia.fr">contact@responsia.fr</a>.
        </p>
        <p className="mt-2">
          Consultez également notre <Link to="/confidentialite">Politique de Confidentialité</Link> et 
          notre page <Link to="/rgpd">Droits RGPD</Link>.
        </p>
      </SectionCard>
    </ModernLegalPage>
  );
};

// ==================== CONTACT PAGE ====================
export const Contact = () => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = React.useState({ type: '', message: '' });
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: 'Votre message a été envoyé avec succès ! Nous vous répondrons sous 24-48h.' });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus({ type: 'error', message: data.detail || 'Une erreur est survenue. Veuillez réessayer.' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Erreur de connexion. Veuillez réessayer plus tard.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModernLegalPage 
      title="Contactez-nous" 
      subtitle="Une question ? Nous sommes là pour vous aider"
      icon={Mail}
      iconBg="from-violet-600 to-pink-500"
    >
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Contact Info Cards */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-violet-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
            <a href="mailto:contact@responsia.fr" className="text-violet-600 hover:underline">
              contact@responsia.fr
            </a>
            <p className="text-sm text-slate-500 mt-2">Réponse sous 24-48h ouvrées</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
              <Phone className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">Téléphone</h3>
            <a href="tel:+33666027925" className="text-violet-600 hover:underline">
              06 66 02 79 25
            </a>
            <p className="text-sm text-slate-500 mt-2">Du lundi au vendredi</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">Horaires</h3>
            <p className="text-slate-600">Lundi - Vendredi</p>
            <p className="text-slate-600">9h00 - 18h00</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">Adresse</h3>
            <p className="text-slate-600">52 Boulevard Murat</p>
            <p className="text-slate-600">75016 Paris, France</p>
          </div>

          <div className="bg-gradient-to-br from-violet-500 to-pink-500 rounded-2xl p-6 text-white">
            <HelpCircle className="w-8 h-8 mb-3 opacity-80" />
            <h3 className="font-semibold mb-2">Besoin d'aide rapide ?</h3>
            <p className="text-white/80 text-sm mb-3">
              Consultez notre FAQ pour trouver des réponses instantanées.
            </p>
            <Link 
              to="/faq" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
            >
              Voir la FAQ
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Link>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-3">
              <Send className="w-5 h-5 text-violet-600" />
              Envoyez-nous un message
            </h2>

            {status.message && (
              <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
                status.type === 'success' 
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {status.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span>{status.message}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all outline-none"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all outline-none"
                    placeholder="jean@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Sujet *
                </label>
                <select
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all outline-none bg-white"
                >
                  <option value="">Sélectionnez un sujet</option>
                  <option value="question">Question générale</option>
                  <option value="demo">Demande de démonstration</option>
                  <option value="support">Support technique</option>
                  <option value="billing">Facturation / Abonnement</option>
                  <option value="partnership">Partenariat</option>
                  <option value="rgpd">Demande RGPD</option>
                  <option value="other">Autre</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Message *
                </label>
                <textarea
                  required
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all outline-none resize-none"
                  placeholder="Décrivez votre demande en détail..."
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Lock className="w-4 h-4" />
                <span>Vos données sont protégées et ne seront jamais partagées.</span>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-violet-600 to-pink-500 text-white rounded-xl font-semibold hover:from-violet-700 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Envoyer le message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </ModernLegalPage>
  );
};

// ==================== RGPD PAGE ====================
export const RGPD = () => {
  const [requestType, setRequestType] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [details, setDetails] = React.useState('');
  const [status, setStatus] = React.useState({ type: '', message: '' });
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_type: requestType, email, details })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: 'Votre demande RGPD a été enregistrée. Nous la traiterons dans un délai maximum de 30 jours.' });
        setRequestType('');
        setEmail('');
        setDetails('');
      } else {
        setStatus({ type: 'error', message: data.detail || 'Une erreur est survenue. Veuillez réessayer.' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Erreur de connexion. Veuillez réessayer plus tard.' });
    } finally {
      setLoading(false);
    }
  };

  const rights = [
    {
      icon: Eye,
      title: "Droit d'accès",
      description: "Obtenir une copie de toutes les données personnelles que nous détenons sur vous.",
      color: "blue"
    },
    {
      icon: FileText,
      title: "Droit de rectification",
      description: "Corriger ou mettre à jour vos données personnelles si elles sont inexactes.",
      color: "emerald"
    },
    {
      icon: Trash2,
      title: "Droit à l'effacement",
      description: "Demander la suppression de vos données personnelles (« droit à l'oubli »).",
      color: "red"
    },
    {
      icon: Download,
      title: "Droit à la portabilité",
      description: "Récupérer vos données dans un format structuré et lisible par machine.",
      color: "violet"
    },
    {
      icon: Ban,
      title: "Droit d'opposition",
      description: "Vous opposer au traitement de vos données à des fins de marketing.",
      color: "orange"
    },
    {
      icon: Lock,
      title: "Droit à la limitation",
      description: "Limiter le traitement de vos données dans certaines circonstances.",
      color: "slate"
    }
  ];

  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-600' }
  };

  return (
    <ModernLegalPage 
      title="Vos droits RGPD" 
      subtitle="Exercez vos droits sur vos données personnelles"
      icon={Shield}
      iconBg="from-emerald-600 to-teal-600"
    >
      <SectionCard title="Le RGPD, c'est quoi ?" icon={HelpCircle}>
        <p>
          Le <strong>Règlement Général sur la Protection des Données (RGPD)</strong> est un règlement 
          européen entré en vigueur le 25 mai 2018. Il renforce et unifie la protection des données 
          personnelles des citoyens de l'Union européenne.
        </p>
        <p className="mt-3">
          Chez ResponsIA, nous prenons la protection de vos données très au sérieux. Cette page vous 
          permet de comprendre vos droits et de les exercer facilement.
        </p>
      </SectionCard>

      <SectionCard title="Vos 6 droits fondamentaux" icon={Shield}>
        <div className="grid md:grid-cols-2 gap-4 not-prose">
          {rights.map((right, idx) => (
            <div key={idx} className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl ${colorClasses[right.color].bg} flex items-center justify-center flex-shrink-0`}>
                  <right.icon className={`w-5 h-5 ${colorClasses[right.color].text}`} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">{right.title}</h4>
                  <p className="text-sm text-slate-500 mt-1">{right.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Exercer vos droits" icon={Send}>
        <p className="mb-6">
          Remplissez le formulaire ci-dessous pour exercer l'un de vos droits RGPD. 
          Nous traiterons votre demande dans un délai maximum de <strong>30 jours</strong>.
        </p>

        {status.message && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 not-prose ${
            status.type === 'success' 
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {status.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span>{status.message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 not-prose">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Type de demande *
            </label>
            <select
              required
              value={requestType}
              onChange={(e) => setRequestType(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none bg-white"
            >
              <option value="">Sélectionnez votre demande</option>
              <option value="access">Droit d'accès - Obtenir mes données</option>
              <option value="rectification">Droit de rectification - Corriger mes données</option>
              <option value="erasure">Droit à l'effacement - Supprimer mes données</option>
              <option value="portability">Droit à la portabilité - Exporter mes données</option>
              <option value="opposition">Droit d'opposition - Arrêter le traitement</option>
              <option value="limitation">Droit à la limitation - Limiter le traitement</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email associé à votre compte *
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
              placeholder="votre@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Détails supplémentaires (optionnel)
            </label>
            <textarea
              rows={4}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none resize-none"
              placeholder="Précisez votre demande si nécessaire..."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-500 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-teal-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Soumettre ma demande
              </>
            )}
          </button>
        </form>
      </SectionCard>

      <SectionCard title="Délégué à la Protection des Données" icon={User}>
        <div className="grid md:grid-cols-2 gap-4 not-prose">
          <InfoCard label="DPO" value="Sabrina ADJARSA" icon={User} />
          <InfoCard label="Email DPO" value="dpo@responsia.fr" icon={Mail} />
          <InfoCard label="Adresse" value="52 Boulevard Murat, 75016 Paris" icon={MapPin} />
        </div>
        <p className="mt-4">
          Vous pouvez également contacter la CNIL (Commission Nationale de l'Informatique et des Libertés) 
          si vous estimez que vos droits ne sont pas respectés :{" "}
          <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
        </p>
      </SectionCard>

      <SectionCard title="Documents associés" icon={FileText}>
        <div className="grid md:grid-cols-2 gap-4 not-prose">
          <Link 
            to="/confidentialite" 
            className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center group-hover:bg-violet-200 transition-colors">
              <Shield className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Politique de Confidentialité</p>
              <p className="text-sm text-slate-500">Comment nous protégeons vos données</p>
            </div>
          </Link>
          <Link 
            to="/cookies" 
            className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors">
              <Cookie className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Gestion des Cookies</p>
              <p className="text-sm text-slate-500">Gérez vos préférences de cookies</p>
            </div>
          </Link>
        </div>
      </SectionCard>
    </ModernLegalPage>
  );
};
